import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Card, CardHeader, CardContent, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { CheckCircle, ShieldCheck } from "@phosphor-icons/react";

/**
 * Public intake form — /intake/:token. No auth required. The token IS
 * the auth. Renders the practice's branding (name/logo/color), not
 * TherapyStack Pro's, per Phase 8 requirement.
 */
export default function IntakePage() {
  const { token } = useParams();
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [form, setForm] = useState({
    student_full_name: "",
    date_of_birth: "",
    guardian_name: "",
    guardian_email: "",
    guardian_phone: "",
    emergency_contact_name: "",
    emergency_contact_phone: "",
    emergency_contact_relationship: "",
    reason_for_therapy: "",
    previous_therapy_experience: "",
    consent_treatment: false,
    consent_hipaa: false,
  });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/intake/public/${token}`);
        setMeta(data);
        if (data.student_name_hint) {
          setForm(f => ({ ...f, student_full_name: data.student_name_hint }));
        }
      } catch (e) {
        setError(formatApiError(e));
      }
    })();
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent_treatment || !form.consent_hipaa) {
      return toast.error("Please confirm both consent statements to continue.");
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/intake/public/${token}/submit`, form);
      setDone(data);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-background">
        <Card className="max-w-md w-full"><CardContent className="p-6 text-center space-y-2">
          <div className="text-lg font-semibold">Link unavailable</div>
          <p className="text-sm text-muted-foreground">{error}</p>
        </CardContent></Card>
      </div>
    );
  }
  if (!meta) {
    return <div className="min-h-screen flex items-center justify-center"><div className="h-10 w-10 rounded-full border-2 border-primary/20 border-t-primary animate-spin" /></div>;
  }

  const p = meta.practice;
  const primary = p.primary_color || "#4A6753";

  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-background">
        <Card className="max-w-md w-full" data-testid="intake-success">
          <CardContent className="p-6 space-y-4 text-center">
            <div className="rounded-full bg-emerald-100 h-16 w-16 mx-auto flex items-center justify-center">
              <CheckCircle size={40} weight="fill" className="text-emerald-600" />
            </div>
            <div className="text-xl font-semibold">Thank you!</div>
            <p className="text-sm text-muted-foreground">
              Your intake has been received by <span className="font-medium">{done.practice_name}</span>.
              We'll be in touch shortly to schedule the first visit.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-8 px-4" data-testid="intake-page">
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Practice header */}
        <div className="flex items-center gap-3">
          {p.logo_url ? (
            <img src={p.logo_url} alt={p.name} className="h-12 w-12 rounded-lg object-cover" />
          ) : (
            <div className="h-12 w-12 rounded-lg flex items-center justify-center text-white font-bold text-lg" style={{ background: primary }}>
              {(p.name || "P")[0]}
            </div>
          )}
          <div>
            <div className="text-xs uppercase tracking-wide text-muted-foreground">Intake form</div>
            <div className="text-xl font-semibold">{p.name}</div>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Tell us about the student</CardTitle>
            <p className="text-sm text-muted-foreground">
              This form takes about 3 minutes and helps us prepare for your first visit.
            </p>
          </CardHeader>
          <CardContent>
            <form onSubmit={submit} className="space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1.5 md:col-span-2">
                  <Label>Student full name *</Label>
                  <Input required data-testid="intake-student-name"
                    value={form.student_full_name}
                    onChange={e => setForm(f => ({ ...f, student_full_name: e.target.value }))} />
                </div>
                <div className="space-y-1.5">
                  <Label>Date of birth *</Label>
                  <Input required type="date" data-testid="intake-dob"
                    value={form.date_of_birth}
                    onChange={e => setForm(f => ({ ...f, date_of_birth: e.target.value }))} />
                </div>
              </div>

              <div className="border-t pt-4 space-y-4">
                <div className="text-sm font-semibold">Parent or guardian</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label>Full name *</Label>
                    <Input required data-testid="intake-guardian-name"
                      value={form.guardian_name}
                      onChange={e => setForm(f => ({ ...f, guardian_name: e.target.value }))} />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Email *</Label>
                    <Input required type="email" data-testid="intake-guardian-email"
                      value={form.guardian_email}
                      onChange={e => setForm(f => ({ ...f, guardian_email: e.target.value }))} />
                  </div>
                  <div className="space-y-1.5 md:col-span-2">
                    <Label>Phone *</Label>
                    <Input required data-testid="intake-guardian-phone"
                      value={form.guardian_phone}
                      onChange={e => setForm(f => ({ ...f, guardian_phone: e.target.value }))} />
                  </div>
                </div>
              </div>

              <div className="border-t pt-4 space-y-4">
                <div className="text-sm font-semibold">Emergency contact</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label>Name *</Label>
                    <Input required data-testid="intake-emerg-name"
                      value={form.emergency_contact_name}
                      onChange={e => setForm(f => ({ ...f, emergency_contact_name: e.target.value }))} />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Phone *</Label>
                    <Input required data-testid="intake-emerg-phone"
                      value={form.emergency_contact_phone}
                      onChange={e => setForm(f => ({ ...f, emergency_contact_phone: e.target.value }))} />
                  </div>
                  <div className="space-y-1.5 md:col-span-2">
                    <Label>Relationship *</Label>
                    <Input required placeholder="e.g. Grandfather, Aunt, Neighbour"
                      data-testid="intake-emerg-rel"
                      value={form.emergency_contact_relationship}
                      onChange={e => setForm(f => ({ ...f, emergency_contact_relationship: e.target.value }))} />
                  </div>
                </div>
              </div>

              <div className="border-t pt-4 space-y-4">
                <div className="space-y-1.5">
                  <Label>Reason for seeking therapy *</Label>
                  <Textarea required rows={3} data-testid="intake-reason"
                    value={form.reason_for_therapy}
                    onChange={e => setForm(f => ({ ...f, reason_for_therapy: e.target.value }))} />
                </div>
                <div className="space-y-1.5">
                  <Label>Previous therapy experience</Label>
                  <Textarea rows={3} placeholder="Optional. Type 'None' if not applicable."
                    data-testid="intake-prev"
                    value={form.previous_therapy_experience}
                    onChange={e => setForm(f => ({ ...f, previous_therapy_experience: e.target.value }))} />
                </div>
              </div>

              <div className="border-t pt-4 space-y-3">
                <div className="text-sm font-semibold flex items-center gap-2">
                  <ShieldCheck size={16} /> Consent
                </div>
                <label className="flex items-start gap-2 text-sm cursor-pointer" data-testid="intake-consent-treatment">
                  <Checkbox
                    checked={form.consent_treatment}
                    onCheckedChange={v => setForm(f => ({ ...f, consent_treatment: !!v }))}
                    className="mt-0.5"
                  />
                  <span>I consent to the treatment of the student named above at {p.name}.</span>
                </label>
                <label className="flex items-start gap-2 text-sm cursor-pointer" data-testid="intake-consent-hipaa">
                  <Checkbox
                    checked={form.consent_hipaa}
                    onCheckedChange={v => setForm(f => ({ ...f, consent_hipaa: !!v }))}
                    className="mt-0.5"
                  />
                  <span>
                    I acknowledge {p.name}'s HIPAA privacy policy
                    {p.hipaa_policy_url && <> (<a href={p.hipaa_policy_url} target="_blank" rel="noreferrer" className="underline" style={{ color: primary }}>read policy</a>)</>}.
                  </span>
                </label>
              </div>

              <Button
                type="submit" disabled={busy}
                data-testid="intake-submit"
                className="w-full text-white"
                style={{ background: primary }}
              >
                {busy ? "Submitting…" : "Submit intake form"}
              </Button>
              <p className="text-[11px] text-muted-foreground text-center">
                Submissions are one-time. If you need to change anything later, ask {p.name} to send a fresh link.
              </p>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
